import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { PanelsTopLeft, SwatchBook } from "lucide-react";
import {
	customizeProduct,
	filterFabricsByGroup,
	getPriceDifference,
	groupFabric,
} from "../../actions/blindActions";
import { addToSwatchCart } from "../../actions/cartActions";
import Customize_Reworked from "./index";
import FabricSeriesHeader from "../../components/FabricSeriesHeader";
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Toast } from "./Toast";

const Customize_Fabric = () => {
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) => state.customize.customizeProduct
	);

	// States
	const [sampleToast, setSampleToast] = useState(false);
	const [fabricGroupFilter, setFabricGroupFilter] = useState(
		productInfo?.Default_Fabric_Type?.toLowerCase() === "all"
			? "All"
			: productInfo?.Default_Fabric_Type?.charAt(0)?.toUpperCase() +
					productInfo?.Default_Fabric_Type?.slice(1)
	);
	const [fabricColourFilter, setFabricColourFilter] = useState("All");
	const [fabricGroups, setFabricGroups] = useState([]);
	const [fabricColours, setFabricColours] = useState([]);
	const [allFabrics, setAllFabrics] = useState([]);
	const [allPrices, setAllPrices] = useState([]);
	const [filteredFabrics, setFilteredFabrics] = useState([]);
	const [loading, setLoading] = useState(true);

	// Selected fabric states
	const [selectedGroup, setSelectedGroup] = useState(productInfo.Fabric_Type);
	const [selectedFabricCode, setSelectedFabricCode] = useState(
		productInfo.Fabric_Code
	);
	const [selectedFabric, setSelectedFabric] = useState(productInfo.Fabric);
	const [selectedFabricColor, setSelectedFabricColor] = useState(
		productInfo.SG_Color
	);
	const [priceGroup, setPriceGroup] = useState(productInfo.Price_Group);
	const [selectedOcsName, setSelectedOcsName] = useState("");
	const [selectedOcsColour, setSelectedOcsColour] = useState("");
	const [selectedOcsDiscount, setSelectedOcsDiscount] = useState(
		productInfo?.Discount
	);

	const swatchCartItems = useSelector(
		(state) => state.swatchCart.swatchCartItems
	);
	const measure = optionInfo.measure;
	const shadeTitle = (productInfo.Type + " Shade").toLowerCase();
	let length = optionInfo.lengthWholeFrac
		? parseFloat(optionInfo.lengthWhole) +
		  parseFloat(optionInfo.lengthWholeFrac)
		: optionInfo.lengthWhole;

	let width = optionInfo.widthWholeFrac
		? parseFloat(optionInfo.widthWhole) + parseFloat(optionInfo.widthWholeFrac)
		: optionInfo.widthWhole;

	if (length % 1 !== 0) {
		length = eval(length);
	}
	if (width % 1 !== 0) {
		width = eval(width);
	}

	// Animation variants
	const cardVariants = {
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: -20 },
	};

	const selectVariants = {
		focus: { scale: 1.02 },
		blur: { scale: 1 },
	};

	const loadGroups = async () => {
		switch (measure) {
			case "Tight":
				width = parseFloat(width) - parseFloat(0.0125);
				break;
			case "Finish":
				width = parseFloat(width);
				break;
			case "Cloth":
				width = parseFloat(width) + parseFloat(1);
				break;
			default:
				width = parseFloat(width);
				break;
		}

		try {
			const groups = (await groupFabric(width, shadeTitle))
				.map((group) => group.charAt(0).toUpperCase() + group.slice(1))
				.filter((group) => group !== "Null");
			console.log(groups);
			setFabricGroups(groups);
			dispatch(
				customizeProduct({
					productInfo: {
						...productInfo,
						error: false,
						loaded: true,
					},
					optionInfo: {
						...optionInfo,
					},
				})
			);
		} catch (error) {
			console.log(error);
			// ERROR: todo
			alert(
				"Invalid length or width. Please go back to details, reduce it and try again."
			);
			window.location.href = "/customize/details";
			dispatch(
				customizeProduct({
					productInfo: {
						...productInfo,
						error: true,
					},
					optionInfo: {
						...optionInfo,
					},
				})
			);
		}
	};

	const loadColours = async () => {
		try {
			const res = await filterFabricsByGroup(
				width,
				shadeTitle,
				productInfo?.Default_Fabric_Type.toLowerCase() === "all"
					? "All"
					: productInfo?.Fabric_Type,
				"All"
			);
			const prices = await getPriceDifference(shadeTitle, length, width);

			let filters = res.seriesColourImage;

			// Process and normalize the data
			filters = filters.map((item) => ({
				...item,
				ocs_name: item.ocs_name,
				image: item.image.map((img, j) => {
					let processedImg = img.replace("image/", "/images/");
					// Check if jpg does not exist in the image name
					if (processedImg.indexOf(".jpg") === -1) {
						processedImg += item.colour[j] + ".jpg";
					}

					return processedImg;
				}),
				// Fix Berkeley spelling if needed
				colour: item.colour.map((c) => (c === "Berkeley" ? "Berkley" : c)),
			}));

			// Group by ocs_name and sort by priceGroup
			const groupedFilters = filters.reduce((acc, item) => {
				// Since ocs_name is an array, we need to handle each series name
				item.ocs_name.forEach((series, index) => {
					if (!acc[series]) {
						acc[series] = [];
					}

					// Create a single-entry version of the item for this specific series
					const singleSeriesItem = {
						...item,
						// For each array property, only take the corresponding index
						colourFamily: [item.colourFamily?.[index]],
						series: [item.series?.[index]],
						colour: [item.colour?.[index]],
						image: [item.image?.[index]],
						code: [item.code?.[index]],
						group: [item.group?.[index]],
						penetration: item.penetration?.[index]
							? [item.penetration[index]]
							: [],
						composition: item.composition?.[index]
							? [item.composition[index]]
							: [],
						fire_rating: item.fire_rating?.[index]
							? [item.fire_rating[index]]
							: [],
						green_rating: item.green_rating?.[index]
							? [item.green_rating[index]]
							: [],
						antimicrobial_rating: item.antimicrobial_rating?.[index]
							? [item.antimicrobial_rating[index]]
							: [],
						ocs_name: [item.ocs_name?.[index]],
						ocs_colour_name: [item.ocs_colour_name?.[index]],
						ocs_discount: item.ocs_discount?.[index]
							? [item.ocs_discount[index]]
							: [],
						priceGroup: item.priceGroup,
					};

					acc[series].push(singleSeriesItem);
				});

				return acc;
			}, {});

			// Sort each group by priceGroup
			Object.keys(groupedFilters).forEach((series) => {
				groupedFilters[series].sort((a, b) => a.priceGroup - b.priceGroup);
			});

			setFabricColours(res.uniqueColourFamily);
			setAllFabrics(filters); // Keep original format for filtering
			setAllPrices(prices);
			setFilteredFabrics(groupedFilters);
			setLoading(false);

			// If productInfo?.Fabric is "", set the first fabric in the list as the default
			if (productInfo?.Fabric === "") {
				// Get first fabric from the grouped structure
				const firstSeriesKey = Object.keys(groupedFilters)[0];
				const firstFabric = groupedFilters[firstSeriesKey][0];

				fabricHandler(
					firstFabric.series[0],
					firstFabric.colour[0],
					firstFabric.code[0],
					firstFabric.group[0],
					firstFabric.priceGroup,
					firstFabric.ocs_name[0],
					firstFabric.ocs_colour_name[0],
					firstFabric.ocs_discount[0]
				);
			}
		} catch (err) {
			console.log(err);
			alert(
				"Invalid length or width. Please go back to details, reduce it and try again."
			);
			window.location.href = "/customize/details";
			dispatch(
				customizeProduct({
					productInfo: {
						...productInfo,
						error: true,
					},
					optionInfo: {
						...optionInfo,
					},
				})
			);
		}
	};

	const filterFabrics = () => {
		// Start with all fabrics
		let filtered = allFabrics;

		// Helper function to extract matching indices and create filtered object
		const filterByProperty = (item, propertyName, filterValue) => {
			const matchingIndices = item[propertyName].reduce(
				(indices, value, index) => {
					if (value.toLowerCase() === filterValue.toLowerCase()) {
						indices.push(index);
					}
					return indices;
				},
				[]
			);

			if (matchingIndices.length === 0) return null;

			// Properties to extract
			const arrayProperties = [
				"colourFamily",
				"series",
				"colour",
				"image",
				"code",
				"group",
				"penetration",
				"composition",
				"fire_rating",
				"green_rating",
				"antimicrobial_rating",
				"ocs_name",
				"ocs_colour_name",
				"ocs_discount",
			];

			// Create new object with filtered arrays
			const filteredItem = arrayProperties.reduce((obj, prop) => {
				obj[prop] = matchingIndices
					.map((index) => item[prop]?.[index])
					.filter(Boolean);
				return obj;
			}, {});

			// Add non-array properties
			filteredItem.priceGroup = item.priceGroup;

			// Verify required properties exist
			const requiredProps = [
				"code",
				"colour",
				"colourFamily",
				"group",
				"image",
				"series",
				"ocs_name",
				"ocs_colour_name",
			];
			const hasAllRequired = requiredProps.every(
				(prop) => filteredItem[prop]?.length > 0
			);

			return hasAllRequired ? filteredItem : null;
		};

		// Apply group filter
		if (fabricGroupFilter !== "All") {
			filtered = filtered
				.map((item) => filterByProperty(item, "group", fabricGroupFilter))
				.filter(Boolean);
		}

		// Apply color filter
		if (fabricColourFilter !== "All") {
			filtered = filtered
				.map((item) =>
					filterByProperty(item, "colourFamily", fabricColourFilter)
				)
				.filter(Boolean);
		}

		// Group by ocs_name and sort by priceGroup
		const groupedAndSorted = filtered.reduce((acc, item) => {
			// Since ocs_name is an array, we need to handle each series name
			item.ocs_name.forEach((series, index) => {
				if (!acc[series]) {
					acc[series] = [];
				}
				console.log(item);
				// Create a single-entry version of the item for this specific series
				const singleSeriesItem = {
					...item,
					// For each array property, only take the corresponding index
					colourFamily: [item.colourFamily[index]],
					series: [item.series[index]],
					colour: [item.colour[index]],
					image: [item.image[index]],
					code: [item.code[index]],
					group: [item.group[index]],
					penetration: item.penetration[index] ? [item.penetration[index]] : [],
					composition: item.composition[index] ? [item.composition[index]] : [],
					fire_rating: item.fire_rating[index] ? [item.fire_rating[index]] : [],
					green_rating: item.green_rating[index]
						? [item.green_rating[index]]
						: [],
					antimicrobial_rating: item.antimicrobial_rating[index]
						? [item.antimicrobial_rating[index]]
						: [],
					ocs_name: [item.ocs_name[index]],
					ocs_colour_name: [item.ocs_colour_name[index]],
					ocs_discount: item.ocs_discount[index]
						? [item.ocs_discount[index]]
						: [],
					priceGroup: item.priceGroup,
				};

				acc[series].push(singleSeriesItem);
			});

			return acc;
		}, {});

		// Sort each group by priceGroup
		Object.keys(groupedAndSorted).forEach((series) => {
			groupedAndSorted[series].sort((a, b) => a.priceGroup - b.priceGroup);
		});

		// Convert back to array format if needed, or use the grouped object directly
		const finalResult = groupedAndSorted;

		setFilteredFabrics(finalResult);
	};

	const dispatchDetails = () => {
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					Fabric_Code: selectedFabricCode,
					Fabric: selectedFabric,
					SG_Color: selectedFabricColor,
					Price_Group: priceGroup,
					Fabric_Type: selectedGroup,
					OCS_Name: selectedOcsName,
					OCS_Colour: selectedOcsColour,
					Discount: selectedOcsDiscount,
				},
				optionInfo: {
					...optionInfo,
					selectedColor: selectedFabricColor,
				},
			})
		);
	};

	const fabricHandler = (
		series,
		colour,
		code,
		group,
		priceGroup,
		ocs_name,
		ocs_colour_name,
		ocs_discount
	) => {
		setSelectedFabric(series);
		setSelectedFabricColor(colour);
		setSelectedFabricCode(code);
		setSelectedGroup(group);
		setPriceGroup(priceGroup);
		setSelectedOcsName(ocs_name);
		setSelectedOcsColour(ocs_colour_name);
		setSelectedOcsDiscount(ocs_discount);
	};

	const existsInCart = (index, img, color, code) => {
		let itemToCheck = {
			shade: shadeTitle,
			name: index + " " + color,
			image: img,
			code: code,
			type: index,
			//Alex TO DO: Make sure this is correct, looks like series: series?
			series: color,
		};
		let exists = false;
		swatchCartItems.forEach((item) => {
			if (
				item.item.name === itemToCheck.name &&
				item.item.shade === itemToCheck.shade &&
				item.item.code === itemToCheck.code &&
				item.item.type === itemToCheck.type &&
				item.item.series === itemToCheck.series
			) {
				exists = true;
			}
		});

		return exists;
	};

	const handleSample = (index, img, color, code, ocs_name, ocs_colour_name) => {
		if (swatchCartItems.length >= 10) {
			alert("You can't add more than 10 fabrics to cart");
			return;
		}
		if (existsInCart(index, img, color, code, ocs_name, ocs_colour_name)) {
			return;
		}
		let item = {
			shade: shadeTitle,
			name: index + " " + color,
			image: img,
			code: code,
			type: index,
			series: color,
			ocs_name: ocs_name,
			ocs_colour_name: ocs_colour_name,
		};
		console.log("Add sample");
		console.log(item);
		dispatch(addToSwatchCart({ item }));

		setSampleToast(true);
	};

	useEffect(() => {
		loadGroups();
		loadColours();
	}, []);

	useEffect(() => {
		filterFabrics();
	}, [fabricGroupFilter, fabricColourFilter]);

	useEffect(() => {
		dispatchDetails();
	}, [selectedFabricCode, selectedFabric, selectedFabricColor, priceGroup]);

	// Main render
	return (
		<Customize_Reworked>
			<div className="space-y-8">
				{/* Toast Notification */}
				<Toast
					show={sampleToast}
					type="success"
					message="Sample swatch added to cart!"
					onClose={() => setSampleToast(false)}
				/>

				{/* Filters Section */}
				<div className="space-y-6">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
							<SwatchBook className="w-6 h-6 text-primary" />
						</div>
						<h3 className="text-lg font-medium">Fabric Filters</h3>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{/* Transparency Filter */}
						<motion.div
							variants={selectVariants}
							whileFocus="focus"
							animate="blur"
						>
							<label className="block text-sm font-medium mb-2">
								By Transparency
							</label>
							<select
								value={fabricGroupFilter}
								onChange={(e) => setFabricGroupFilter(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none appearance-none transition-all"
							>
								{productInfo?.Default_Fabric_Type.toLowerCase() === "all" && (
									<option value="All">All</option>
								)}
								{fabricGroups
									.filter((group) =>
										productInfo?.Default_Fabric_Type.toLowerCase() !== "all"
											? group.toLowerCase() ===
											  productInfo.Fabric_Type.toLowerCase()
											: true
									)
									.map((group, i) => (
										<option key={`${group}-${i}`} value={group}>
											{group}
										</option>
									))}
							</select>
						</motion.div>

						{/* Color Filter */}
						<motion.div
							variants={selectVariants}
							whileFocus="focus"
							animate="blur"
						>
							<label className="block text-sm font-medium mb-2">
								By Colour
							</label>
							<select
								aria-label="Fabric Colour Filter"
								value={fabricColourFilter}
								onChange={(e) => setFabricColourFilter(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none appearance-none transition-all"
							>
								<option value="All">All</option>
								{fabricColours.map((color) => (
									<option key={color} value={color}>
										{color}
									</option>
								))}
							</select>
						</motion.div>
					</div>
				</div>

				{/* Fabrics Grid */}
				<div className="space-y-6">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
							<PanelsTopLeft className="w-6 h-6 text-primary" />
						</div>
						<h3 className="text-lg font-medium">Available Fabrics</h3>
					</div>

					<div className="max-h-[60vh] overflow-y-auto pr-4 space-y-8">
						{loading ? (
							<div className="flex items-center justify-center h-32">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
							</div>
						) : Object.keys(filteredFabrics).length === 0 ? (
							<div className="text-center py-8 text-gray-500">
								No fabrics found matching your filters
							</div>
						) : (
							Object.entries(filteredFabrics).map(
								([seriesName, fabricsInSeries]) => (
									<motion.div
										key={seriesName}
										variants={cardVariants}
										initial="initial"
										animate="animate"
										exit="exit"
										className="space-y-4"
									>
										{console.log(
											Object.entries(filteredFabrics)[0][1][0].priceGroup ===
												fabricsInSeries[0].priceGroup
										)}
										{/* Fabric Series Header - using first item in series for header info */}
										<FabricSeriesHeader
											title={fabricsInSeries[0].series[0]}
											headerInfo={{
												ocs_name: seriesName,
												firstPG:
													Object.entries(filteredFabrics)[0][1][0].priceGroup,
												included:
													Object.entries(filteredFabrics)[0][1][0]
														.priceGroup === fabricsInSeries[0].priceGroup,
												allPrices: allPrices,
												group:
													fabricsInSeries[0].group[0]?.charAt(0).toUpperCase() +
													fabricsInSeries[0].group[0]?.slice(1),
												priceGroup: fabricsInSeries[0].priceGroup,
												composition: fabricsInSeries[0].composition[0],
												green_rating: fabricsInSeries[0].green_rating[0],
												fire_rating: fabricsInSeries[0].fire_rating[0],
												antimicrobial_rating:
													fabricsInSeries[0].antimicrobial_rating[0],
												// disc: fabricsInSeries[0].ocs_discount[0],
												disc: 0.0,
												globalDisc: productInfo.Global_Discount,
											}}
											className="bg-white shadow-sm rounded-xl p-4"
										/>

										{/* Fabric Swatches Grid */}
										<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
											{fabricsInSeries.map((fabric) => (
												<motion.div
													key={fabric.code[0]}
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													className={`
                  rounded-xl transition-all
                  ${
										selectedFabricCode === fabric.code[0]
											? "border-2 border-primary shadow-lg shadow-primary/10"
											: "hover:border-primary/30"
									}
                `}
												>
													<div
														className="cursor-pointer"
														aria-label={`Select ${fabric.series[0]} ${fabric.colour[0]}`}
														onClick={() =>
															fabricHandler(
																fabric.series[0],
																fabric.colour[0],
																fabric.code[0],
																fabric.group[0],
																fabric.priceGroup,
																fabric.ocs_name[0],
																fabric.ocs_colour_name[0],
																fabric.ocs_discount[0]
															)
														}
													>
														<ImageZoomWithButton
															item={{
																image: fabric.image[0],
																code: fabric.code[0],
																colour: fabric.colour[0],
															}}
															isFabricSelected={
																selectedFabricCode === fabric.code[0]
															}
															customize={true}
														/>
														<div className="p-3 text-center">
															<p className="font-medium">
																{fabric.ocs_colour_name[0] || fabric.colour[0]}
															</p>
														</div>
													</div>

													<button
														onClick={() =>
															handleSample(
																fabric.series[0],
																fabric.image[0],
																fabric.colour[0],
																fabric.code[0],
																fabric.ocs_name[0],
																fabric.ocs_colour_name[0]
															)
														}
														className={`
                    w-full py-2 text-sm font-medium transition-colors rounded-md
                    ${
											existsInCart(
												fabric.series[0],
												fabric.image[0],
												fabric.colour[0],
												fabric.code[0]
											)
												? "bg-primary/40 text-white"
												: "bg-primary/10 text-primary hover:bg-primary/20"
										}
                  `}
													>
														{existsInCart(
															fabric.series[0],
															fabric.image[0],
															fabric.colour[0],
															fabric.code[0]
														)
															? "Added"
															: "Free Sample"}
													</button>
												</motion.div>
											))}
										</div>
									</motion.div>
								)
							)
						)}
					</div>
				</div>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Fabric;
